import { FunctionComponent, memo, useContext } from "react";
import { ModalContext } from "@edgetier/modal";
import TagGroupForm from "components-for/tags/tag-group-form";
import toast from "react-hot-toast";
import { getServerError } from "utilities/get-server-error";
import { ITagGroupFormValues } from "components-for/tags/tag-group-form/tag-group-form.types";
import { axios } from "utilities/axios";
import { ITagGroup, ITagGroupPostRequest, Route } from "@edgetier/watchtower-types";
import { useQueryClient } from "react-query";
import { IProps } from "./create-tag-group.types";
import TagsListContext from "pages/tag-phrase-triggers/tags-list/tags-list-context/tags-list-context";
import { trackUser } from "utilities/track-user";
import "./create-tag-group.scss";

/**
 * A form for creating a new tag group. When submitted, the new tag group is POSTed to the backend.
 */
const CreateTagGroup: FunctionComponent<IProps> = () => {
    const queryClient = useQueryClient();
    const { closeModal } = useContext(ModalContext);
    const { tagGroupType } = useContext(TagsListContext);

    /**
     * POST the new tag group to the backend.
     * @param values The values for the new tag group.
     */
    const onSubmit = async (values: ITagGroupFormValues) => {
        try {
            const { data } = await axios.post<ITagGroupPostRequest>(Route.TagGroups, {
                isUnaryForAgents: values.isUnaryForAgents,
                isUnaryForInteractions: values.isUnaryForInteractions,
                isExternalEnabled: values.isExternalEnabled,
                tagGroup: values.tagGroup,
                tagGroupType: tagGroupType,
            });
            queryClient.setQueriesData<ITagGroup[]>(Route.TagGroups, (tagGroups) => {
                return [...(tagGroups ?? []), { ...data }];
            });
            trackUser("Create Tag Group");
            toast.success(`"${values.tagGroup}" has been created successfully.`);
            closeModal();
        } catch (error) {
            toast.error(getServerError(error));
        }
    };

    return (
        <TagGroupForm
            initialValues={{
                tagGroupId: null,
                isCommonFilter: false,
                isExternalEnabled: false,
                isUnaryForInteractions: false,
                isUnaryForAgents: false,
                tagGroup: "",
            }}
            onSubmit={onSubmit}
        />
    );
};

export default memo(CreateTagGroup);
